import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import { Delete as DeleteIcon, Edit as EditIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
export function PDFPinMarker({
  pin,
  onDelete,
  onUpdateLabel,
  onRecordingClick,
  onRecordingPlay,
  onUpdatePosition,
  onEditPosition,
  onPinClick,
  recordings,
  isEditingPosition,
  currentSession
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [editedLabel, setEditedLabel] = useState(pin.label);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Extract pin number from label (e.g., "Pin 1" -> 1)
  const pinNumber = pin.label.match(/\d+/)?.[0] || '1';

  // Use L-number if available, fallback to extracted number
  const displayNumber = pin.lNumber || `L${pinNumber}`;

  // Get recording details if this is a recording pin
  const recording = recordings?.find(r => r.id === pin.recordingId);

  // Format recording name: dd-mmm-yy-hh-mm
  const formatRecordingName = rec => {
    const date = new Date(rec.timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}-${month}-${year}-${hours}-${minutes}`;
  };
  const handleClick = event => {
    if (!isEditingPosition) {
      if (onPinClick) {
        // If onPinClick is provided, call it instead of opening popover
        onPinClick(pin);
      } else {
        // Fallback to popover if no onPinClick handler
        setAnchorEl(event.currentTarget);
      }
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    setIsEditing(false);
    setEditedLabel(pin.label);
  };
  const handleSaveLabel = () => {
    onUpdateLabel(pin.id, editedLabel);
    setIsEditing(false);
  };
  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };
  const handleConfirmDelete = deleteRecording => {
    onDelete(pin.id, deleteRecording);
    setShowDeleteDialog(false);
    handleClose();
  };
  const handleEditPosition = () => {
    onEditPosition?.(pin.id);
    handleClose();
  };
  const open = Boolean(anchorEl);
  const isRecordingPin = pin.type === 'recording';

  // Determine if this pin is from the current session or historical
  const isHistoricalPin = currentSession !== undefined && pin.sessionNumber !== undefined && pin.sessionNumber !== currentSession;

  // Pin colors: historical sessions = orange, current session = red
  const pinColor = isHistoricalPin ? '#FF8555' : '#ea4335';
  const recordingPinColor = isHistoricalPin ? '#FF8555' : '#d32f2f';
  return <>
      <Box onClick={handleClick} sx={{
      position: 'absolute',
      left: `${pin.position.x}%`,
      top: `${pin.position.y}%`,
      transform: 'translate(-50%, -100%)',
      cursor: isEditingPosition ? 'move' : 'pointer',
      zIndex: isEditingPosition ? 100 : 5,
      transition: isEditingPosition ? 'none' : 'transform 0.2s ease',
      pointerEvents: isEditingPosition ? 'none' : 'auto',
      '&:hover': {
        transform: isEditingPosition ? 'translate(-50%, -100%)' : 'translate(-50%, -100%) scale(1.15)'
      }
    }}>
        {/* Edit Mode Pulsing Ring */}
        {isEditingPosition && <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        border: '3px solid #2932e5',
        animation: 'pulse 1.5s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': {
            opacity: 1,
            transform: 'translate(-50%, -50%) scale(1)'
          },
          '50%': {
            opacity: 0.5,
            transform: 'translate(-50%, -50%) scale(1.2)'
          }
        }
      }} />}
        
        {/* Pin Icon */}
        <Box sx={{
        position: 'relative',
        width: 32,
        height: 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
          {/* Google Maps style pin */}
          <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '50% 50% 50% 0',
          bgcolor: isRecordingPin ? recordingPinColor : pinColor,
          transform: 'rotate(-45deg)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid white'
        }}>
            <Box sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: 'white',
            transform: 'rotate(45deg)'
          }} />
          </Box>

          {/* L-Number Badge at Base */}
          <Box sx={{
          mt: 0.5,
          px: 0.75,
          py: 0.25,
          bgcolor: 'white',
          border: '1px solid #212121',
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
        }}>
            <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '10px',
            fontWeight: 700,
            color: '#212121',
            lineHeight: 1
          }}>
              {displayNumber}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Pin Details Popover */}
      <Popover open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'center'
    }} sx={{
      '& .MuiPopover-paper': {
        minWidth: 280,
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e5e7eb',
        mt: 1
      }
    }}>
        <Box sx={{
        p: 2
      }}>
          {/* Comprehensive Header with L-Number */}
          <Box sx={{
          mb: 1.5,
          pb: 1.5,
          borderBottom: '1px solid #e5e7eb'
        }}>
            <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 600,
            color: '#2932e5',
            mb: 0.5
          }}>
              Location: {displayNumber}
            </Typography>
            <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '11px',
            color: '#4a5565'
          }}>
              {pin.type === 'recording' ? 'Recording Pin' : 'Manual Pin'} • Session {pin.sessionNumber || 1}
            </Typography>
            <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '11px',
            color: '#4a5565'
          }}>
              Page {pin.pageNumber} • {new Date(pin.timestamp).toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
            </Typography>
            <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '10px',
            color: '#4a5565',
            mt: 0.5
          }}>
              Drawing: {pin.drawingPath?.split('/').pop() || 'Unknown'}
            </Typography>
          </Box>

          {/* Label */}
          {isEditing ? <TextField fullWidth variant="outlined" size="medium" placeholder="Enter label" value={editedLabel} onChange={e => setEditedLabel(e.target.value)} autoFocus helperText="Pin Label" FormHelperTextProps={{
          sx: {
            mt: 0,
            mx: 0
          }
        }} sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': {
            padding: 0
          }
        }} /> : <Typography onClick={() => setIsEditing(true)} sx={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: '13px',
          color: '#212121',
          mb: 1.5,
          cursor: 'pointer',
          p: 1,
          borderRadius: '4px',
          bgcolor: '#f2f2f2',
          '&:hover': {
            bgcolor: '#e5e7eb'
          }
        }}>
              {pin.label || 'Click to add label'}
            </Typography>}

          {/* Recording Link - with formatted name and play icon */}
          {pin.recordingId && recording && <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          p: 1,
          borderRadius: '4px',
          bgcolor: '#ffe0d0',
          mb: 1.5
        }}>
              <Typography onClick={() => {
            onRecordingPlay?.(pin.recordingId);
            handleClose();
          }} sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '12px',
            color: '#212121',
            cursor: 'pointer',
            flex: 1,
            '&:hover': {
              textDecoration: 'underline'
            }
          }}>
                🎥 {formatRecordingName(recording)}
              </Typography>
              <IconButton size="small" onClick={e => {
            e.stopPropagation();
            onRecordingPlay?.(pin.recordingId);
            handleClose();
          }} sx={{
            bgcolor: '#2932e5',
            color: 'white',
            width: 24,
            height: 24,
            '&:hover': {
              bgcolor: '#1f26b8'
            }
          }}>
                <PlayArrowIcon sx={{
              fontSize: 16
            }} />
              </IconButton>
            </Box>}

          {/* Action Buttons */}
          <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'flex-end'
        }}>
            {isEditing && <Button size="small" onClick={handleSaveLabel} sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '13px',
            color: '#2932e5'
          }}>
                Save
              </Button>}
            <Button size="small" onClick={handleEditPosition} startIcon={<EditIcon sx={{
            fontSize: 16
          }} />} sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '13px',
            color: '#2932e5'
          }}>
              Edit Pin
            </Button>
            <Button size="small" onClick={handleDeleteClick} startIcon={<DeleteIcon sx={{
            fontSize: 16
          }} />} sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '13px',
            color: '#d32f2f'
          }}>
              Delete Pin
            </Button>
            <Button size="small" onClick={handleClose} sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '13px',
            color: '#4a5565'
          }}>
              Close
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} sx={{
      '& .MuiDialog-paper': {
        borderRadius: '8px',
        maxWidth: '400px'
      }
    }}>
        <DialogTitle sx={{
        fontFamily: 'Roboto, sans-serif',
        fontSize: '18px',
        fontWeight: 600,
        color: '#212121'
      }}>
          Delete Pin
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: '14px',
          color: '#4a5565'
        }}>
            {pin.recordingId ? <>
                This pin has an associated recording. Do you want to delete the recording as well?
              </> : <>
                Are you sure you want to delete this pin?
              </>}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }}>
          <Button onClick={() => setShowDeleteDialog(false)} sx={{
          textTransform: 'none',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '13px',
          color: '#4a5565'
        }}>
            Cancel
          </Button>
          {pin.recordingId ? <>
              <Button onClick={() => handleConfirmDelete(false)} sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '13px',
            color: '#2932e5'
          }}>
                Delete Pin Only
              </Button>
              <Button onClick={() => handleConfirmDelete(true)} startIcon={<DeleteIcon sx={{
            fontSize: 16
          }} />} sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '13px',
            color: '#d32f2f'
          }}>
                Delete Both
              </Button>
            </> : <Button onClick={() => handleConfirmDelete(false)} startIcon={<DeleteIcon sx={{
          fontSize: 16
        }} />} sx={{
          textTransform: 'none',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '13px',
          color: '#d32f2f'
        }}>
              Delete
            </Button>}
        </DialogActions>
      </Dialog>
    </>;
}